import {
  managementModalStyle,
  StyledModal,
} from "pages/PatientOverview/styles";
import { Box, Grid } from "@mui/material";
import { POSITIONS } from "constants/positions";
import { CircularProgress } from "@material-ui/core";
import { icons } from "constants/icons";
import { Button } from "components/Buttons/Button";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { useAppSelector, useAppDispatch } from "hooks/reduxHooks";
import { selectCurrentClinicianLoading } from "store/ClinicianStore/selectors";
import { useEffect, useState } from "react";
import { FilterType } from "constants/types";
import { DatePicker } from "components/DatePicker/DatePicker";
import { MultiSelect } from "components/SportSelect/MultiSelect";
import { FilterSelects } from "components/FilterSelects/FilterSelects";
import {
  examTypeArray,
  minValuesArray,
  athleteMinValuesArray,
} from "constants/clinic";
import moment from "moment";
import { getCoachTeams } from "repositories/teamsApi";
import { FilterDetailsParams } from "types/common";
import { getPatientRegionsList } from "store/PatientRegionsStore/actions";
import { getClinicianPatientsList } from "store/ClinicianStore/actions";
import { setOffset as setPatientRegionsOffset } from "store/PatientRegionsStore/patientRegionsSlice";

type Props = {
  isEnabled: boolean;
  setIsEnabled: any;
  operation: FilterType;
  currentClinician?: any;
  tags?: any;
  onItemSelected?: any;
  applyFilter?: any;
  resetFilter?: any;
  inputStyle?: any;
  fromSection?: string;
  hideInputs?: string[];
  customConfirm?: (data: any) => void;
  customReset?: () => void;
};

type FilterConfig = {
  sports: never[];
  teams: never[];
  positions: never[];
  genders: never[];
  examTypes: never[];
  timeRange: string;
};

export const FilterModal = ({
  isEnabled,
  setIsEnabled,
  operation,
  currentClinician,
  hideInputs,
  onItemSelected,
  tags,
  resetFilter,
  inputStyle,
  customReset,
  customConfirm,
}: Props) => {
  const loading = useAppSelector(selectCurrentClinicianLoading);
  const dispatch = useAppDispatch();
  const [teamList, setTeamList] = useState([]);
  const [fullTeams, setFullTeams] = useState([]);
  const [athleteFilters, setAthleteFilters] = useState({
    sports: [],
    teams: [],
    positions: [],
    genders: [],
    examTypes: [],
    timeRange: "",
  });

  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Date | null>(null);
  const [examTypeSelectedItems, setExamTypeSelectedItems] = useState<string[]>(
    []
  );
  const [minValuesSelectedItems, setMinValuesSelectedItems] = useState<
    string[]
  >([]);

  const setAthleteFiltersFunction = (
    field: string,
    value: never,
    isArray?: boolean
  ) => {
    if (!isArray) {
      const newFilters = { ...athleteFilters, [field]: value };
      window.localStorage.setItem("athleteFilters", JSON.stringify(newFilters));
      return setAthleteFilters(newFilters);
    }

    type FilterKeys =
      | "sports"
      | "teams"
      | "positions"
      | "genders"
      | "examTypes";
    const currentValue = [...athleteFilters[field as FilterKeys]];
    if (currentValue.includes(value)) {
      const index = currentValue.indexOf(value);
      currentValue.splice(index, 1);
    } else {
      currentValue.push(value);
    }

    const newFilters = {
      ...athleteFilters,
      [field]: currentValue,
    };
    window.localStorage.setItem("athleteFilters", JSON.stringify(newFilters));
    setAthleteFilters(newFilters);
  };

  useEffect(() => {
    if (moment(selectedFromDate).isAfter(moment(selectedToDate))) {
      setSelectedToDate(selectedFromDate);
    }
  }, [selectedFromDate]);

  const getTeams = async () => {
    if (!currentClinician) return;
    try {
      const fetchedTeams = await getCoachTeams(currentClinician._id);
      const teamNames = fetchedTeams.map((team: any) => team.name);
      setTeamList(teamNames);
      setFullTeams(fetchedTeams);
    } catch (error) {
      setTeamList([]);
    }
  };

  useEffect(() => {
    getTeams();
  }, []);

  const stringifyArray = (array: never[]) => {
    return (
      "[" +
      array
        .map((item: string) => {
          return item.trim();
        })
        .join(",") +
      "]"
    );
  };

  useEffect(() => {
    if (window.localStorage.getItem("athleteFilters")) {
      const filters = JSON.parse(
        window.localStorage.getItem("athleteFilters") ?? ""
      );
      setAthleteFilters(filters);
      if (
        filters.sports.length ||
        filters.teams.length ||
        filters.positions.length ||
        filters.genders.length ||
        filters.examTypes.length
      ) {
        setTimeout(() => {
          if (customConfirm) {
            customConfirm(parsePayload(filters));
            setIsEnabled(false);
          }
        }, 2000);
      }
    }

    /*     const leftPage = () => {
      window.localStorage.removeItem("athleteFilters");
    };

    window.addEventListener("beforeunload", leftPage);
    return () => {
      window.removeEventListener("beforeunload", leftPage);
    }; */
  }, []);

  const onConfirm = () => {
    setIsEnabled(false);

    dispatch(setPatientRegionsOffset(0));

    const detailedFilter: FilterDetailsParams = {};

    if (selectedFromDate) {
      detailedFilter.startDate = moment(selectedFromDate)
        .startOf("day")
        .toDate();
    }
    if (selectedToDate) {
      detailedFilter.endDate = moment(selectedToDate).endOf("day").toDate();
    }

    if (minValuesSelectedItems && minValuesSelectedItems.length > 0) {
      detailedFilter.examMinRange =
        "['" +
        minValuesSelectedItems
          .map((item) => {
            const modifiedItem = item.replace("to ", "").replace("below", "");
            return modifiedItem;
          })
          .join(",") +
        "']";
    }

    if (FilterType.ATHLETE_FILTER === operation) {
      if (tags && tags.length > 0) {
        detailedFilter.sports =
          "[" +
          tags
            .map((item: string) => {
              return item.trim();
            })
            .join(",") +
          "]";
      }
    } else {
      if (examTypeSelectedItems && examTypeSelectedItems.length > 0) {
        detailedFilter.examType =
          "[" +
          examTypeSelectedItems
            .map((item) => {
              const parts = item.split(" | ");
              const side = parts[0].split(" ")[0].toLowerCase();
              const locations = parts[1].split(" ");
              const location = locations[locations.length - 1].toLowerCase();
              return `${side} ${location}`;
            })
            .join(",") +
          "]";
      }
    }

    if (FilterType.ATHLETE_FILTER === operation) {
      if (Object.keys(detailedFilter).length === 0) {
        dispatch(getPatientRegionsList());
      } else {
        dispatch(getPatientRegionsList(undefined, detailedFilter));
      }
    } else {
      if (Object.keys(detailedFilter).length === 0) {
        dispatch(getClinicianPatientsList(currentClinician?._id ?? 0));
      } else {
        dispatch(
          getClinicianPatientsList(
            currentClinician?._id ?? 0,
            () => {},
            undefined,
            detailedFilter
          )
        );
      }
    }
  };

  const resetFiltersInModal = () => {
    setIsEnabled(false);

    setMinValuesSelectedItems([]);
    setExamTypeSelectedItems([]);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    resetFilter();
    window.localStorage.removeItem("athleteFilters");
    setAthleteFilters({
      sports: [],
      teams: [],
      positions: [],
      genders: [],
      examTypes: [],
      timeRange: "",
    });
    if (operation == FilterType.ATHLETE_FILTER) {
      if (customReset) {
        customReset();
      }
      dispatch(getPatientRegionsList(undefined, {}));
    }
  };

  const parsePayload = (currentFilters: any) => {
    return {
      sports: currentFilters?.sports.length
        ? stringifyArray(currentFilters.sports)
        : undefined,
      teams: currentFilters.teams.length
        ? stringifyArray(currentFilters.teams)
        : undefined,
      positions: currentFilters.positions.length
        ? stringifyArray(currentFilters.positions)
        : undefined,
      examTypes: currentFilters.examTypes.length
        ? stringifyArray(currentFilters.examTypes)
        : undefined,
      genders: currentFilters.genders.length
        ? stringifyArray(currentFilters.genders)
        : undefined,
      timeRange: currentFilters.timeRange || undefined,
      fromFilterModal: true,
    };
  };

  const athleteConfirm = (filters?: FilterConfig) => {
    let currentFilters = filters || athleteFilters;
    setIsEnabled(false);
    let minRange = "";
    if (minValuesSelectedItems && minValuesSelectedItems.length > 0) {
      minRange =
        "['" +
        minValuesSelectedItems
          .map((item) => {
            const modifiedItem = item.replace("to ", "").replace("below", "");
            return modifiedItem;
          })
          .join(",") +
        "']";
    }
    dispatch(
      getPatientRegionsList(undefined, {
        ...parsePayload(currentFilters),
        examMinRange: minValuesSelectedItems.length ? minRange : undefined,
      })
    );
  };

  const athleteFilterStyle = {
    ...inputStyle,
    maxWidth: "100%",
  };
  return (
    <StyledModal
      aria-labelledby="unstyled-modal-title"
      aria-describedby="unstyled-modal-description"
      open={isEnabled}
    >
      <Box
        sx={
          window.location.pathname.includes("leaderboard")
            ? { ...managementModalStyle, height: "fit-content" }
            : { ...managementModalStyle }
        }
      >
        {loading ? (
          <CircularProgress color="inherit" size={20} />
        ) : (
          <>
            <Grid
              container
              mt={1}
              mb={1}
              sx={{
                marginTop: window.location.pathname.includes("leaderboard")
                  ? "30px"
                  : "200px",
              }}
            >
              <Grid item xs={11} sx={{ fontWeight: "400", fontSize: 16 }}>
                {"Filter"}
              </Grid>
              <Grid item xs={1}>
                <img
                  src={icons.modalClose}
                  alt="close"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setIsEnabled(false);
                  }}
                  aria-hidden="true"
                />
              </Grid>
            </Grid>
            {FilterType.ATHLETE_FILTER === operation && (
              <div
                style={{
                  width: "100%",
                }}
              >
                <Grid container xs={12} mt={3} flexDirection={"column"}>
                  <Grid
                    item
                    sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                  >
                    {"Filter by Sport"}
                  </Grid>
                  <MultiSelect
                    options={currentClinician?.profileInfo?.sports ?? []}
                    selectedOptions={athleteFilters.sports ?? []}
                    onItemSelected={(_: any, value: never) => {
                      setAthleteFiltersFunction("sports", value, true);
                    }}
                    paddingTop={0}
                    hint={
                      athleteFilters.sports && athleteFilters.sports.length > 0
                        ? athleteFilters.sports.toString()
                        : "Sport list"
                    }
                    menuSubTitle={"(M = Men's, W = Women's)"}
                    menuTitle={""}
                    style={athleteFilterStyle}
                    emptyMessage="Please select sport categories in settings page"
                  />
                </Grid>
                <Grid container xs={12} mt={3} flexDirection={"column"}>
                  <Grid
                    item
                    sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                  >
                    {"Filter by Team"}
                  </Grid>
                  <MultiSelect
                    options={teamList ?? []}
                    selectedOptions={athleteFilters.teams ?? []}
                    onItemSelected={(_: any, value: never) => {
                      setAthleteFiltersFunction("teams", value, true);
                    }}
                    paddingTop={0}
                    hint={
                      athleteFilters.teams && athleteFilters.teams.length > 0
                        ? athleteFilters.teams.toString()
                        : "Team list"
                    }
                    menuSubTitle={"(M = Men's, W = Women's)"}
                    menuTitle={""}
                    style={athleteFilterStyle}
                    emptyMessage="Please select team categories in settings page"
                  />
                </Grid>
                <Grid container xs={12} mt={3} flexDirection={"column"}>
                  <Grid
                    item
                    sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                  >
                    {"Filter by Position"}
                  </Grid>
                  <MultiSelect
                    options={POSITIONS}
                    selectedOptions={athleteFilters.positions ?? []}
                    onItemSelected={(_: any, value: never) =>
                      setAthleteFiltersFunction("positions", value, true)
                    }
                    paddingTop={0}
                    hint={
                      athleteFilters.positions &&
                      athleteFilters.positions.length > 0
                        ? athleteFilters.positions.toString()
                        : "Position list"
                    }
                    menuSubTitle={"(M = Men's, W = Women's)"}
                    menuTitle={""}
                    style={athleteFilterStyle}
                    emptyMessage=""
                  />
                </Grid>
                <Grid container xs={12} mt={3} flexDirection={"column"}>
                  <Grid
                    item
                    sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                  >
                    {"Filter by Gender"}
                  </Grid>
                  <MultiSelect
                    options={["Male", "Female", "Other"]}
                    selectedOptions={athleteFilters.genders ?? []}
                    onItemSelected={(_: any, value: never) =>
                      setAthleteFiltersFunction("genders", value, true)
                    }
                    paddingTop={0}
                    hint={
                      athleteFilters.genders &&
                      athleteFilters.genders.length > 0
                        ? athleteFilters.genders.toString()
                        : "Gender list"
                    }
                    menuTitle={""}
                    style={athleteFilterStyle}
                    emptyMessage=""
                  />
                </Grid>
                {!hideInputs?.includes("examTypes") && (
                  <Grid container xs={12} mt={3} flexDirection={"column"}>
                    <Grid
                      item
                      sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                    >
                      {"Filter by Exam Type"}
                    </Grid>
                    <MultiSelect
                      options={[
                        "Double Knee Drop",
                        "Single Knee Drop - Left Knee",
                        "Single Knee Drop - Right Knee",
                      ]}
                      selectedOptions={athleteFilters.examTypes ?? []}
                      onItemSelected={(_: any, value: never) =>
                        setAthleteFiltersFunction("examTypes", value, true)
                      }
                      paddingTop={0}
                      hint={
                        athleteFilters.examTypes &&
                        athleteFilters.examTypes.length > 0
                          ? athleteFilters.examTypes.toString()
                          : "Exam types list"
                      }
                      menuTitle={""}
                      style={athleteFilterStyle}
                      emptyMessage=""
                    />
                  </Grid>
                )}

                <Grid container xs={12} mt={3} flexDirection={"column"}>
                  <Grid
                    item
                    sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                  >
                    {"Filter by Time range"}
                  </Grid>
                  <MultiSelect
                    options={[
                      "Last 7 days",
                      "Last 30 days",
                      "Last 3 months",
                      "Last 6 months",
                      "Last year",
                    ]}
                    selectedOptions={[athleteFilters.timeRange]}
                    onItemSelected={(_: any, value: never) =>
                      setAthleteFiltersFunction("timeRange", value, false)
                    }
                    paddingTop={0}
                    hint={
                      athleteFilters.timeRange &&
                      athleteFilters.timeRange.length > 0
                        ? athleteFilters.timeRange.toString()
                        : "Time range list"
                    }
                    menuTitle={""}
                    style={athleteFilterStyle}
                    emptyMessage=""
                  />
                </Grid>

                {!hideInputs?.includes("minValue") && (
                  <Grid container xs={12} mt={2} flexDirection={"column"}>
                    <Grid
                      item
                      sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                    >
                      {"Minimum Value"}
                    </Grid>
                    <FilterSelects
                      items={athleteMinValuesArray}
                      selectedItems={minValuesSelectedItems}
                      setSelectedItems={setMinValuesSelectedItems}
                    />
                  </Grid>
                )}
              </div>
            )}

            {FilterType.PATIENT_FILTER === operation && (
              <>
                <Grid container xs={12} mt={3} flexDirection={"column"}>
                  <Grid
                    item
                    sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                  >
                    {"Last Exam"}
                  </Grid>
                  <Grid item paddingBottom={1}>
                    <DatePicker
                      onChange={(date: Date) => {
                        setSelectedFromDate(date);
                      }}
                      hint="From"
                      date={selectedFromDate}
                    />
                  </Grid>
                  <Grid item paddingBottom={1}>
                    <DatePicker
                      onChange={(date: Date) => {
                        setSelectedToDate(date);
                      }}
                      hint="To"
                      minDate={selectedFromDate ?? new Date()}
                      date={selectedToDate}
                    />
                  </Grid>
                </Grid>
                <Grid container xs={12} mt={2} flexDirection={"column"}>
                  <Grid
                    item
                    sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                  >
                    {"Exam Type"}
                  </Grid>
                  <FilterSelects
                    items={examTypeArray}
                    selectedItems={examTypeSelectedItems}
                    setSelectedItems={setExamTypeSelectedItems}
                  />
                </Grid>
                <Grid container xs={12} mt={2} flexDirection={"column"}>
                  <Grid
                    item
                    sx={{ fontWeight: "900", fontSize: 14, paddingBottom: 1 }}
                  >
                    {"Minimum Value for the Recent Exam"}
                  </Grid>
                  <FilterSelects
                    items={minValuesArray}
                    selectedItems={minValuesSelectedItems}
                    setSelectedItems={setMinValuesSelectedItems}
                  />
                </Grid>
              </>
            )}
          </>
        )}
        <Grid xl={12} container sx={{ justifyContent: "flex-end", mt: 2 }}>
          <Button
            title={"Reset"}
            onClick={resetFiltersInModal}
            type={ButtonType.text}
            disabled={false}
            style={{ color: "#9D9EA5", marginRight: 20 }}
          />
          <Button
            title={"Confirm"}
            style={{ maxHeight: "40px" }}
            onClick={
              customConfirm
                ? () => {
                    customConfirm(parsePayload(athleteFilters));
                    setIsEnabled(false);
                  }
                : FilterType.ATHLETE_FILTER === operation
                ? () => athleteConfirm()
                : () => onConfirm()
            }
            type={ButtonType.normal}
            colorScheme={ButtonColorScheme.gradient}
            disabled={false}
          />
        </Grid>
      </Box>
    </StyledModal>
  );
};
